import PageBlockToolbarWrapper from "molecules/page-block-content/page-block-toolbar-wrapper";
import { RichTextEditorProps } from "molecules/rich-text/rich-text-editor";
import PageBlockContextMenu from "organisms/page-blocks/page-block-list/page-block-context-menu";
import React, { ChangeEvent } from "react";
import {
    NotebookTextBlockQuillConfig,
    ToolbarButtonConfig,
    ToolbarDropdownOptionConfig,
} from "utilities/quill/quill-config";

const CSS_CLASS_NAME = "c-text-block-rich-text-editor";

// -------------------------------------------------------------------------------------------------
// #region Interfaces
// -------------------------------------------------------------------------------------------------

export interface NotebookTextBlockRickTextEditorToolbarProps
    extends Required<
        Pick<RichTextEditorProps, "allowImages" | "allowReferenceLinks">
    > {
    disabled?: boolean;
    id: string;
    onDeleteClick?: () => Promise<void>;
    onMoveUp?: () => Promise<void>;
    onMoveDown?: () => Promise<void>;
    onDone?: () => Promise<void>;
}

// #endregion Interfaces

// -------------------------------------------------------------------------------------------------
// #region Component
// -------------------------------------------------------------------------------------------------

/**
 * Custom HTML Toolbar for quill. Quill automatically
 * attaches events based on classnames.
 * @param props {NotebookTextBlockRickTextEditorToolbarProps}
 */
const NotebookTextBlockRickTextEditorToolbar: React.FC<
    NotebookTextBlockRickTextEditorToolbarProps
> = (props: NotebookTextBlockRickTextEditorToolbarProps) => {
    const { disabled, id, onDeleteClick, onDone, onMoveDown, onMoveUp } = props;

    const persistSelectChangeEvent = (e: ChangeEvent<HTMLSelectElement>) =>
        e.persist();

    const disabledModifier = disabled ? "-disabled" : "";

    return (
        <PageBlockToolbarWrapper onDone={onDone}>
            <div
                className={`${CSS_CLASS_NAME}__toolbar ql-toolbar ql-${NotebookTextBlockQuillConfig.theme} ${disabledModifier}`}
                id={id}>
                <span
                    className={
                        NotebookTextBlockQuillConfig.ToolbarButtonClasses
                            .Section
                    }>
                    <select
                        aria-label={"Apply heading"}
                        className={
                            NotebookTextBlockQuillConfig.ToolbarButtonClasses
                                .Header
                        }
                        defaultValue={""}
                        onChange={persistSelectChangeEvent}>
                        {NotebookTextBlockQuillConfig.headerFormats.map(
                            (cfg: ToolbarDropdownOptionConfig) => (
                                <option value={cfg.value} key={cfg.value} />
                            )
                        )}
                        <option />
                    </select>
                </span>
                <span
                    className={
                        NotebookTextBlockQuillConfig.ToolbarButtonClasses
                            .Section
                    }>
                    {NotebookTextBlockQuillConfig.inlineFormats.map(
                        (cfg: ToolbarButtonConfig) => (
                            <button
                                aria-label={cfg.accessibleText}
                                className={`ql-${cfg.format}`}
                                key={`${cfg.format}-${cfg.value}`}
                                value={cfg.value}
                                type="button"
                            />
                        )
                    )}
                </span>
                <span
                    className={
                        NotebookTextBlockQuillConfig.ToolbarButtonClasses
                            .Section
                    }>
                    {NotebookTextBlockQuillConfig.scriptFormats.map(
                        (cfg: ToolbarButtonConfig) => (
                            <button
                                aria-label={cfg.accessibleText}
                                className={`ql-${cfg.format}`}
                                key={`${cfg.format}-${cfg.value}`}
                                value={cfg.value}
                                type="button"
                            />
                        )
                    )}
                    {NotebookTextBlockQuillConfig.equationFormats.map(
                        (cfg: ToolbarButtonConfig) => (
                            <button
                                aria-label={cfg.accessibleText}
                                className={`ql-${cfg.format}`}
                                key={`${cfg.format}-${cfg.value}`}
                                value={cfg.value}
                                type="button"
                            />
                        )
                    )}
                </span>
                <span
                    className={
                        NotebookTextBlockQuillConfig.ToolbarButtonClasses
                            .Section
                    }>
                    <select
                        aria-label={"Align text"}
                        className={
                            NotebookTextBlockQuillConfig.ToolbarButtonClasses
                                .Align
                        }
                        defaultValue={""}
                        onChange={persistSelectChangeEvent}>
                        <option />
                        {NotebookTextBlockQuillConfig.alignmentFormats.map(
                            (cfg: ToolbarDropdownOptionConfig) => (
                                <option value={cfg.value} key={cfg.value} />
                            )
                        )}
                    </select>
                    <button
                        aria-label={"Toggle bulleted list"}
                        className={
                            NotebookTextBlockQuillConfig.ToolbarButtonClasses
                                .List
                        }
                        type={"button"}
                        value={"bullet"}
                    />
                    <button
                        aria-label={"Toggle numbered list"}
                        className={
                            NotebookTextBlockQuillConfig.ToolbarButtonClasses
                                .List
                        }
                        type={"button"}
                        value={"ordered"}
                    />
                    {NotebookTextBlockQuillConfig.indentFormats.map(
                        (cfg: ToolbarButtonConfig) => (
                            <button
                                aria-label={cfg.accessibleText}
                                className={`ql-${cfg.format}`}
                                key={`${cfg.format}-${cfg.value}`}
                                value={cfg.value}
                                type="button"
                            />
                        )
                    )}
                </span>
                <span
                    className={
                        NotebookTextBlockQuillConfig.ToolbarButtonClasses
                            .Section
                    }>
                    <button
                        aria-label={"Link selected text"}
                        className={
                            NotebookTextBlockQuillConfig.ToolbarButtonClasses
                                .Link
                        }
                        type={"button"}
                    />
                    <PageBlockContextMenu
                        inline
                        mobileOnly
                        onDelete={onDeleteClick}
                        onMoveUp={onMoveUp}
                        onMoveDown={onMoveDown}
                    />
                </span>
            </div>
        </PageBlockToolbarWrapper>
    );
};

// #endregion Component

// -------------------------------------------------------------------------------------------------
// #region Exports
// -------------------------------------------------------------------------------------------------

export default NotebookTextBlockRickTextEditorToolbar;

// #endregion Exports
